import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { timeout } from 'rxjs/operators';
import { ApiService } from './api.service';
import { LoggerService } from './logger.service';
import { StorageService } from '../storage.service';

export interface ISessionCheckResponse {
  SignedInUsers: number;
  RemainingUsers: number;
  HasExistingSession: boolean;
}

const DEFAULT_SERVER_TIMEOUT = 5000;

@Injectable({
  providedIn: 'root'
})
export class LoadBalancerService {
  private assignments: Map<string, string> = new Map();

  constructor(private http: HttpClient, private apiService: ApiService, private loggerService: LoggerService, private storageService: StorageService) {
    this.hydrateFromLocalStorage();
  }

  /**
   * Given an array of server URLs (e.g. ["https://server1/CCSCloudPlugin", "https://server2/CCSCloudPlugin"]),
   * queries each server's active session endpoint and selects the best one.
   * Selection priority:
   * 1. A server where the user already has an existing session (least-loaded if multiple)
   * 2. The server with the most remaining capacity (excluding 0-capacity servers)
   * Returns the selected server URL or null if none are available.
   */
  async selectServer(pluginName: string, serverUrls: string[], sessionCheckPath?: string, serverTimeout?: number): Promise<string | null> {
    // Return cached assignment if already resolved for this plugin
    const cached = this.assignments.get(pluginName);
    if (cached) {
      return cached;
    }

    const username = await this.apiService.getApiUserName();
    if (!username) {
      this.loggerService.logger.logError('LoadBalancerService: No username available for session check');
      return serverUrls[0] || null;
    }

    const timeoutMs = serverTimeout && serverTimeout > 0 ? serverTimeout : DEFAULT_SERVER_TIMEOUT;
    const checkPath = sessionCheckPath || '/api/activesessions';

    const results: { url: string; response: ISessionCheckResponse | null }[] = [];

    const checks = serverUrls.map(async (url) => {
      try {
        const endpoint = `${url}${checkPath}`;
        const response = await this.http.post<ISessionCheckResponse>(endpoint, { Username: username }).pipe(timeout(timeoutMs)).toPromise();
        results.push({ url, response });
      } catch (error) {
        this.loggerService.logger.logWarning(`LoadBalancerService: Server ${url} unreachable or timed out (${timeoutMs}ms). Error: ${JSON.stringify(error)}`);
        results.push({ url, response: null });
      }
    });

    await Promise.all(checks);

    // First priority: server(s) where user already has a session
    const existingSessions = results.filter((r) => r.response?.HasExistingSession);

    if (existingSessions.length === 1) {
      const selected = existingSessions[0].url;
      this.loggerService.logger.logDebug(`LoadBalancerService: User "${username}" has existing session on ${selected}`);
      this.persistAssignment(pluginName, selected);
      return selected;
    }

    if (existingSessions.length > 1) {
      // Multiple existing sessions: pick least-loaded, terminate the others
      existingSessions.sort((a, b) => (a.response?.SignedInUsers ?? 0) - (b.response?.SignedInUsers ?? 0));
      const selected = existingSessions[0].url;
      this.loggerService.logger.logDebug(
        `LoadBalancerService: User "${username}" has sessions on ${existingSessions.length} servers. Selecting least-loaded: ${selected}`
      );
      this.persistAssignment(pluginName, selected);

      // Terminate sessions on the other server(s)
      for (let i = 1; i < existingSessions.length; i++) {
        this.terminateSession(existingSessions[i].url, checkPath, username);
      }
      return selected;
    }

    // Second priority: server with the most remaining capacity (exclude 0-capacity)
    const available = results
      .filter((r) => r.response !== null && r.response.RemainingUsers > 0)
      .sort((a, b) => (b.response?.RemainingUsers ?? 0) - (a.response?.RemainingUsers ?? 0));

    if (available.length > 0) {
      const selected = available[0].url;
      const remaining = available[0].response?.RemainingUsers ?? 0;
      this.loggerService.logger.logDebug(`LoadBalancerService: Selected ${selected} with ${remaining} remaining capacity`);
      this.persistAssignment(pluginName, selected);
      return selected;
    }

    const excludedZeroCapacity = results.filter((r) => r.response !== null && r.response.RemainingUsers === 0);
    if (excludedZeroCapacity.length > 0) {
      this.loggerService.logger.logWarning(`LoadBalancerService: ${excludedZeroCapacity.length} server(s) excluded due to 0 available capacity`);
    }

    this.loggerService.logger.logError('LoadBalancerService: No servers available with remaining capacity');
    return null;
  }

  /**
   * Clears all cached server assignments and localStorage entries (called on logout).
   */
  clearAssignments(): void {
    try {
      this.storageService.clearLoadBalancerAssignments();
    } catch (e) {
      this.loggerService.logger.logWarning(`LoadBalancerService: Failed to clear assignments from localStorage: ${e}`);
    } finally {
      this.assignments.clear();
    }
  }

  /**
   * Gets the current assignment for a plugin, if any.
   */
  getAssignment(pluginName: string): string | null {
    return this.assignments.get(pluginName) || null;
  }

  private persistAssignment(pluginName: string, url: string): void {
    this.assignments.set(pluginName, url);
    try {
      this.storageService.setLoadBalancerAssignment(pluginName, url);
    } catch (e) {
      this.loggerService.logger.logWarning(`LoadBalancerService: Failed to persist assignment to localStorage: ${e}`);
    }
  }

  private hydrateFromLocalStorage(): void {
    try {
      for (const [pluginName, url] of this.storageService.getLoadBalancerAssignments()) {
        if (url) {
          this.assignments.set(pluginName, url);
        }
      }
    } catch (e) {
      // Non-critical — start fresh if localStorage is corrupted
    }
  }

  private terminateSession(serverUrl: string, checkPath: string, username: string): void {
    const endpoint = `${serverUrl}${checkPath}/terminate`;
    this.http
      .post(endpoint, { Username: username })
      .pipe(timeout(DEFAULT_SERVER_TIMEOUT))
      .subscribe({
        next: () => {
          this.loggerService.logger.logDebug(`LoadBalancerService: Terminated orphaned session on ${serverUrl} for user "${username}"`);
        },
        error: (err) => {
          this.loggerService.logger.logWarning(`LoadBalancerService: Failed to terminate session on ${serverUrl}. Error: ${JSON.stringify(err)}`);
        }
      });
  }
}
